import React, { useState, useEffect } from "react";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconEye,
  IconDotsVertical,
  IconTrash,
  IconCalendar,
  IconDownload,
  IconUpload,
  IconArrowLeft,
  IconClock,
  IconCheck,
  IconFileText,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import { 
  fetchHrEmployees,
  fetchHrLoanAdvances,
  createHrLoanAdvance,
  updateHrLoanAdvance,
  deleteHrLoanAdvance
} from "../../../apis/hrApi";
import toast from "react-hot-toast";

export default function LoanAndAdvances() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [activeView, setActiveView] = useState<"list" | "details">("list");
  
  // Right Sidebar Drawer layout toggles
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [rightPanelType, setRightPanelType] = useState<"add" | "import" | "filter">("add");
  
  // Row menus togglers
  const [openRowMenuId, setOpenRowMenuId] = useState<any | null>(null);
  const [openStatusDropdownId, setOpenStatusDropdownId] = useState<any | null>(null);

  // Stateful lists
  const [loansList, setLoansList] = useState<any[]>([]);
  const [selectedLoan, setSelectedLoan] = useState<any | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState<"overview" | "history">("overview");

  // Dynamic Employees dropdown mapping list
  const [employees, setEmployees] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Add Form Inputs
  const [formEmployeeId, setFormEmployeeId] = useState("");
  const [formLoanType, setFormLoanType] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formReason, setFormReason] = useState("");

  // Filters State
  const [filterLoanType, setFilterLoanType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterApprovedBy, setFilterApprovedBy] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Comment dialog states
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [targetLoanId, setTargetLoanId] = useState<string | null>(null);
  const [targetStatusOption, setTargetStatusOption] = useState<string | null>(null);
  const [managerCommentText, setManagerCommentText] = useState("");
  const [commentError, setCommentError] = useState(false);

  // Fetch HR employees on mount
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const res = await fetchHrEmployees();
        if (res.success && res.data?.employees) {
          const mapped = res.data.employees.map((emp: any) => ({
            id: emp.id || emp._id,
            name: emp.firstName ? `${emp.firstName} ${emp.lastName || ""}` : emp.name || "Unknown",
            email: emp.workEmail || emp.personalEmail || emp.email || "-",
            phone: emp.phoneNumber || emp.phone || "-",
            gender: emp.gender || "male",
            birthDate: emp.birthDate ? new Date(emp.birthDate).toLocaleDateString() : "-",
            role: emp.designationName || emp.designation || "-",
          }));
          setEmployees(mapped);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadEmployees();
  }, []);

  // Fetch HR Loan/Advance requests from API
  const loadLoanAdvances = async () => {
    setIsLoading(true);
    try {
      const res = await fetchHrLoanAdvances();
      if (res.success && res.data && Array.isArray(res.data.loanAdvances)) {
        if (res.data.loanAdvances.length === 0) {
          setLoansList([]);
          return;
        }
        
        const mapped = res.data.loanAdvances.map((item: any) => {
          const empName = item.employee?.firstName 
            ? `${item.employee.firstName} ${item.employee.lastName || ""}` 
            : item.employeeName || item.employee?.name || "Unknown";
          
          const cDate = item.createdAt ? new Date(item.createdAt) : new Date();
          const formattedCreatedDate = cDate.toLocaleDateString("en-GB").replace(/\//g, "-");
          const formattedCreatedTime = cDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
          
          const uDate = item.updatedAt ? new Date(item.updatedAt) : new Date();
          const formattedUpdatedDate = uDate.toLocaleDateString("en-GB").replace(/\//g, "-");
          const formattedUpdatedTime = uDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
          
          const appDate = item.approvedDate ? new Date(item.approvedDate).toLocaleDateString("en-GB").replace(/\//g, "-") : "-";

          const mapStatus = (st: string) => {
            if (!st) return "Pending";
            const lower = st.toLowerCase();
            return lower.charAt(0).toUpperCase() + lower.slice(1);
          };

          return {
            id: item.id || item._id,
            date: item.loanDate ? new Date(item.loanDate).toLocaleDateString("en-GB").replace(/\//g, "-") : formattedCreatedDate,
            loanId: item.loanId || `L&A#${String(item.id || item._id || "").slice(-4)}`,
            type: item.loanType === "ADVANCE" ? "Advance" : "Loan",
            name: empName,
            amount: `₹ ${Number(item.amount || 0).toLocaleString()}`,
            balance: item.balance ? `₹ ${Number(item.balance).toLocaleString()}` : "-",
            approvedBy: item.processedBy?.firstName 
              ? `${item.processedBy.firstName} ${item.processedBy.lastName || ""}` 
              : item.processedBy?.name || "-",
            approvedDate: appDate,
            status: mapStatus(item.status),
            createdDate: formattedCreatedDate,
            createdTime: formattedCreatedTime,
            updatedDate: formattedUpdatedDate,
            updatedTime: formattedUpdatedTime,
            reason: item.reason || "-",
            repayment: item.repaymentMethod || "EMI",
            duration: String(item.durationInMonths || 1),
            emiDate: item.emiStartDate ? new Date(item.emiStartDate).toLocaleDateString("en-GB").replace(/\//g, "-") : "-",
            email: item.employee?.workEmail || item.employee?.email || "-",
            personalEmail: item.employee?.personalEmail || "-",
            phone: item.employee?.phoneNumber || item.employee?.phone || "-",
            gender: item.employee?.gender || "male",
            birthDate: item.employee?.birthDate ? new Date(item.employee.birthDate).toLocaleDateString() : "-",
            role: item.employee?.designationName || item.employee?.designation || "-",
            raw: item
          };
        });
        setLoansList(mapped);
      }
    } catch (err) {
      console.error("Failed to load loan advances:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load backend data on component load
  useEffect(() => {
    loadLoanAdvances();
  }, []);

  // Handle Add Form Submission
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formEmployeeId || !formLoanType || !formAmount || !formReason) {
      toast.error("Please fill all required fields");
      return;
    }

    const isLoan = formLoanType === "Loan";
    const payload: any = {
      employeeId: formEmployeeId,
      loanType: isLoan ? "LOAN" : "ADVANCE",
      amount: Number(formAmount).toFixed(2),
      reason: formReason,
      status: "PENDING"
    };

    if (isLoan) {
      payload.repaymentMethod = "EMI";
      payload.durationInMonths = 6;
      payload.emiAmount = (Number(formAmount) / 6).toFixed(2);
      payload.emiStartDate = new Date().toISOString().split("T")[0];
    } else {
      payload.repaymentDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    }

    const loader = toast.loading("Creating loan request...");
    try {
      const res = await createHrLoanAdvance(payload);
      if (res.success) {
        toast.success("Loan request created successfully", { id: loader });
        setIsRightPanelOpen(false);
        // Clear form
        setFormEmployeeId("");
        setFormLoanType("");
        setFormAmount("");
        setFormReason("");
        loadLoanAdvances();
      } else {
        toast.error(res.message || "Failed to create loan request", { id: loader });
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to create loan request", { id: loader });
    }
  };

  // Handle Status Update dropdown patches
  const handleStatusUpdate = async (loanId: string, statusOption: string, commentText: string) => {
    const statusMap: any = {
      Pending: "PENDING",
      Approved: "APPROVED",
      Completed: "COMPLETED",
      Rejected: "REJECTED",
      Closed: "CLOSED"
    };

    const loader = toast.loading("Updating status...");
    try {
      const res = await updateHrLoanAdvance(loanId, {
        status: statusMap[statusOption],
        comments: commentText,
        managerComments: commentText
      });
      if (res.success) {
        toast.success(`Status updated to ${statusOption}`, { id: loader });
        loadLoanAdvances();
      } else {
        toast.error(res.message || "Failed to update status", { id: loader });
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update status", { id: loader });
    }
  };

  // Handle deleting a request
  const handleDeleteRequest = async (loanId: string) => {
    const loader = toast.loading("Deleting request...");
    try {
      const res = await deleteHrLoanAdvance(loanId);
      if (res.success) {
        toast.success("Request deleted successfully", { id: loader });
        loadLoanAdvances();
      } else {
        toast.error(res.message || "Failed to delete request", { id: loader });
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete request", { id: loader });
    }
  };

  // Filter & Search calculation logic
  const filteredLoans = loansList.filter((loan) => {
    // 1. Search Query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchSearch =
        loan.loanId.toLowerCase().includes(q) ||
        loan.name.toLowerCase().includes(q) ||
        loan.type.toLowerCase().includes(q) ||
        loan.status.toLowerCase().includes(q);
      if (!matchSearch) return false;
    }

    // 2. Filter Loan Type
    if (filterLoanType && loan.type !== filterLoanType) return false;

    // 3. Filter Status
    if (filterStatus && loan.status !== filterStatus) return false;

    // 4. Filter Approved By
    if (filterApprovedBy) {
      if (!loan.approvedBy.toLowerCase().includes(filterApprovedBy.toLowerCase())) return false;
    }

    // 5. Filter Start & End Date ranges
    if (filterStartDate) {
      const startParts = filterStartDate.split("-");
      const startObj = new Date(Number(startParts[0]), Number(startParts[1]) - 1, Number(startParts[2]));
      const loanParts = loan.date.split("-");
      const loanObj = new Date(Number(loanParts[2]), Number(loanParts[1]) - 1, Number(loanParts[0]));
      if (loanObj < startObj) return false;
    }
    if (filterEndDate) {
      const endParts = filterEndDate.split("-");
      const endObj = new Date(Number(endParts[0]), Number(endParts[1]) - 1, Number(endParts[2]));
      const loanParts = loan.date.split("-");
      const loanObj = new Date(Number(loanParts[2]), Number(loanParts[1]) - 1, Number(loanParts[0]));
      if (loanObj > endObj) return false;
    }

    return true;
  });

  const columns = [
    {
      label: (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
      render: () => (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
    },
    { label: "DATE", render: (s: any) => <span className="text-xs text-gray-600 font-mono">{s.date}</span> },
    { label: "LOAN ID", render: (s: any) => <span className="font-semibold text-xs text-[#212121]">{s.loanId}</span> },
    { label: "LOAN TYPE", render: (s: any) => <span className="text-xs text-gray-600">{s.type}</span> },
    {
      label: "NAME",
      render: (s: any) => (
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-[#FBD037]/20 border border-[#FBD037]/40 flex items-center justify-center text-[10px] font-bold flex-shrink-0 text-[#212121]">
            {s.name.charAt(0)}
          </div>
          <span className="font-semibold text-xs text-[#212121]">
            {s.name}
          </span>
        </div>
      ),
    },
    { label: "AMOUNT", render: (s: any) => <span className="font-semibold text-xs text-[#212121]">{s.amount}</span> },
    { label: "BALANCE", render: (s: any) => <span className="text-xs text-gray-600">{s.balance}</span> },
    { label: "APPROVED BY", render: (s: any) => <span className="text-xs text-gray-600">{s.approvedBy}</span> },
    { label: "APPROVED DATE", render: (s: any) => <span className="text-xs text-gray-600">{s.approvedDate}</span> },
    {
      label: "STATUS",
      render: (s: any) => {
        const getStatusColor = (status: string) => {
          switch (status) {
            case "Approved": return "text-emerald-700 bg-emerald-50 border-emerald-200/50 hover:bg-emerald-100/50";
            case "Completed": return "text-green-700 bg-green-50 border-green-200/50 hover:bg-green-100/50";
            case "Rejected": return "text-rose-700 bg-rose-50 border-rose-200/50 hover:bg-rose-100/50";
            case "Closed": return "text-purple-700 bg-purple-50 border-purple-200/50 hover:bg-purple-100/50";
            case "Disbursed": return "text-teal-700 bg-teal-50 border-teal-200/50 hover:bg-teal-100/50";
            default: return "text-amber-700 bg-amber-50 border-amber-200/50 hover:bg-amber-100/50"; // Pending
          }
        };
        
        const dotColors = {
          Pending: "#D97706",
          Approved: "#10B981",
          Completed: "#047857",
          Rejected: "#EF4444",
          Closed: "#8B5CF6",
          Disbursed: "#0D9488"
        };

        const isOpen = openStatusDropdownId === s.id;
        return (
          <div className={`relative ${isOpen ? "z-30" : "z-10"}`}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenStatusDropdownId(openStatusDropdownId === s.id ? null : s.id);
              }}
              className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border shadow-sm flex items-center gap-1 cursor-pointer transition select-none ${getStatusColor(s.status)}`}
            >
              <span>● {s.status}</span>
              <svg className="w-2.5 h-2.5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {openStatusDropdownId === s.id && (
              <div className="absolute left-0 mt-1 w-32 bg-white rounded-xl shadow-lg border border-gray-150 py-1.5 z-50 text-left animate-in fade-in slide-in-from-top-1 duration-150 font-semibold text-slate-700">
                {["Pending", "Approved", "Completed", "Rejected", "Closed"].map((statusOption) => (
                  <button
                    key={statusOption}
                    type="button"
                    onClick={() => {
                      setTargetLoanId(s.id);
                      setTargetStatusOption(statusOption);
                      setManagerCommentText("");
                      setCommentError(false);
                      setCommentModalOpen(true);
                      setOpenStatusDropdownId(null);
                    }}
                    className="w-full text-left px-3 py-1.5 text-[11px] font-bold hover:bg-slate-50 transition flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex items-center gap-1.5">
                      <span style={{ color: dotColors[statusOption as keyof typeof dotColors] }}>●</span>
                      <span className="text-gray-700">{statusOption}</span>
                    </span>
                    {s.status === statusOption && (
                      <svg className="w-3.5 h-3.5 text-emerald-600 font-bold" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      }
    },
    {
      label: "CREATED AT",
      render: (s: any) => (
        <div className="text-[10px] font-medium text-gray-500">
          <div>{s.createdDate}</div>
          <div className="text-gray-400 font-semibold">{s.createdTime}</div>
        </div>
      ),
    },
    {
      label: "UPDATED AT",
      render: (s: any) => (
        <div className="text-[10px] font-medium text-gray-500">
          <div>{s.updatedDate}</div>
          <div className="text-gray-400 font-semibold">{s.updatedTime}</div>
        </div>
      ),
    },
    {
      label: "ACTIONS",
      render: (s: any) => (
        <div className={`relative flex justify-center ${openRowMenuId === s.id ? "z-30" : "z-10"}`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpenRowMenuId(openRowMenuId === s.id ? null : s.id);
            }}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#FBD037] hover:bg-[#FBD037]/10 transition cursor-pointer"
          >
            <IconDotsVertical size={16} className="text-gray-500" />
          </button>
          
          {openRowMenuId === s.id && (
            <div className="absolute right-0 top-9 w-32 bg-white rounded-xl shadow-lg border border-gray-150 py-1.5 z-40 animate-in fade-in slide-in-from-top-2 duration-150 font-semibold">
              <button
                type="button"
                onClick={() => {
                  setOpenRowMenuId(null);
                  setSelectedLoan(s);
                  setActiveDetailTab("overview");
                  setActiveView("details");
                }}
                className="w-full text-left px-3.5 py-2 text-xs font-bold text-gray-700 hover:bg-slate-55 transition flex items-center gap-2 cursor-pointer"
              >
                <IconEye size={14} className="text-gray-400" />
                View
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpenRowMenuId(null);
                  handleDeleteRequest(s.id);
                }}
                className="w-full text-left px-3.5 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition flex items-center gap-2 cursor-pointer border-t border-gray-100"
              >
                <IconTrash size={14} className="text-red-400" />
                Delete
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div 
      className="w-full mx-auto mt-0 font-sans" 
      onClick={() => {
        setOpenRowMenuId(null);
        setOpenStatusDropdownId(null);
      }}
    >
      {isLoading && (
        <div className="fixed inset-0 bg-white/40 backdrop-blur-[1px] z-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FBD037]" />
        </div>
      )}

      {activeView === "list" ? (
        <div className="grid grid-cols-12 gap-6 w-full">
          
          {/* Main Table Content Container */}
          <div className={`${
            isRightPanelOpen
              ? "col-span-12 xl:col-span-7"
              : "col-span-12"
          } bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl rounded-b-3xl rounded-tr-3xl border border-white/20 shadow-sm overflow-visible mb-6 transition-all duration-300`} style={{ minHeight: "150px" }}>
            
            {/* Main List Toolbar Controls */}
            <div className="px-6 py-1.5 flex flex-col lg:flex-row lg:items-center justify-end gap-4 border-b border-slate-50">

              <div className="flex items-center flex-wrap gap-2.5">
                {/* Filter toggle button */}
                <button
                  onClick={() => {
                    setRightPanelType("filter");
                    setIsRightPanelOpen(true);
                  }}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg border transition shadow-sm cursor-pointer ${
                    isRightPanelOpen && rightPanelType === "filter"
                      ? "bg-[#FBD037] text-[#212121] border-[#FBD037]"
                      : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                  title="Filter"
                >
                  <IconFilter size={16} stroke={2.5} />
                </button>

                {/* Search Sliding Input Box */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-44 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                        if (showSearch) setSearchQuery("");
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0 cursor-pointer"
                    >
                      <IconSearch size={15} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          placeholder="Search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                        />
                        {searchQuery && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchQuery("");
                            }}
                            className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0 cursor-pointer"
                          >
                            <IconX size={13} stroke={2.5} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Reset/Refresh button */}
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setShowSearch(false);
                    setFilterLoanType("");
                    setFilterStatus("");
                    setFilterApprovedBy("");
                    setFilterStartDate("");
                    setFilterEndDate("");
                  }}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition shadow-sm cursor-pointer"
                  title="Reset"
                >
                  <IconRefresh size={15} stroke={2.5} />
                </button>

                {/* Import button */}
                <button
                  onClick={() => {
                    setRightPanelType("import");
                    setIsRightPanelOpen(true);
                  }}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg border transition shadow-sm cursor-pointer ${
                    isRightPanelOpen && rightPanelType === "import"
                      ? "bg-[#FBD037] text-[#212121] border-[#FBD037]"
                      : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                  }`}
                  title="Import Data"
                >
                  <IconUpload stroke={2.5} size={16} />
                </button>

                {/* Export button */}
                <button
                  onClick={() => {
                    toast.success("Data export initiated successfully");
                  }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition shadow-sm cursor-pointer"
                  title="Export Data"
                >
                  <IconDownload stroke={2.5} size={16} />
                </button>

                {/* Add/Apply button */}
                <button
                  onClick={() => {
                    setRightPanelType("add");
                    setIsRightPanelOpen(true);
                  }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                  title="Apply Request"
                >
                  <IconPlus stroke={2.5} size={16} className="text-[#212121]" />
                </button>
              </div>
            </div>

            {/* Table Body */}
            <div className="border-t border-slate-100 rounded-b-3xl overflow-hidden bg-white">
              <ListView
                columns={columns}
                data={filteredLoans}
                trClassName="hover:bg-[#FBD037]/10 group transition-colors duration-150 border-b border-gray-100 cursor-pointer"
                mainDivClassName="bg-[#F7F6F2] rounded-3xl"
                mailDivClassName="custom-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner max-h-[75vh] min-h-[250px]"
                scrollbarClassName="custom-scrollbar"
                thClassName="text-[11px] font-semibold tracking-wider uppercase select-none text-[#131327]/70"
              />
              {filteredLoans.length === 0 && (
                <div className="text-center py-12 text-sm text-gray-400 font-medium">
                  No loan records match selected filters.
                </div>
              )}
              
              {/* Pagination placeholder */}
              <div className="py-4 flex justify-between items-center px-6">
                <span className="text-xs text-gray-400 font-bold">
                  Showing {filteredLoans.length} of {loansList.length} records
                </span>
                <div className="flex items-center space-x-2">
                  <button className="p-1 rounded-md border text-gray-400 hover:bg-gray-55 disabled:opacity-50" disabled>
                    <IconChevronLeft size={18} />
                  </button>
                  <button className="px-2.5 py-0.5 rounded-md bg-[#FBD037] text-[#212121] font-bold text-xs shadow-sm">1</button>
                  <button className="p-1 rounded-md border text-gray-400 hover:bg-gray-55">
                    <IconChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
                {/* Backdrop on mobile */}
          {isRightPanelOpen && (
            <div
              className="fixed inset-0 bg-black/40 z-30 xl:hidden animate-in fade-in duration-200"
              onClick={() => setIsRightPanelOpen(false)}
            />
          )}

          {/* Right Sidebar Panel */}
          {isRightPanelOpen && (
            <div className="col-span-12 xl:col-span-5 space-y-4 mt-2 xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
              <div className="bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm rounded-3xl p-5 relative flex flex-col w-full">
                
                {/* Header */}
                <div className="flex items-center justify-between pb-3.5 border-b border-gray-100 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
                      <IconCalendar size={16} className="text-[#212121]" stroke={1.5} />
                    </div>
                    <h2 className="text-lg font-bold text-[#212121]">
                      {rightPanelType === "add"
                        ? "Add Loan / Advance"
                        : rightPanelType === "import"
                        ? "Import Data"
                        : "Filter Dashboard"}
                    </h2>
                  </div>
                  <button
                    onClick={() => setIsRightPanelOpen(false)}
                    className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer bg-white"
                  >
                    <IconX size={16} />
                  </button>
                </div>

                {/* Form Content */}
                <div className="space-y-4 text-left">
                  {rightPanelType === "add" && (
                    <form onSubmit={handleAddSubmit} className="space-y-4">
                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
                          Employee <span className="text-red-500">*</span>
                        </label>
                        <select
                          required
                          value={formEmployeeId}
                          onChange={(e) => setFormEmployeeId(e.target.value)}
                          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                        >
                          <option value="">Select Employee</option>
                          {employees.map((emp) => (
                            <option key={emp.id} value={emp.id}>{emp.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
                          Loan Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          required
                          value={formLoanType}
                          onChange={(e) => setFormLoanType(e.target.value)}
                          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                        >
                          <option value="">Select Loan Type</option>
                          <option value="Loan">Loan</option>
                          <option value="Advance">Advance</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
                          Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          required
                          placeholder="Enter amount"
                          value={formAmount}
                          onChange={(e) => setFormAmount(e.target.value)}
                          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
                          Reason <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          required
                          rows={4}
                          placeholder="Add a brief reason..."
                          value={formReason}
                          onChange={(e) => setFormReason(e.target.value)}
                          className="w-full rounded-xl py-2 px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold resize-none"
                        />
                      </div>

                      {/* Action buttons */}
                      <div className="pt-2 flex justify-end gap-2.5">
                        <button
                          type="button"
                          onClick={() => setIsRightPanelOpen(false)}
                          className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer bg-white"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm bg-black hover:bg-slate-800 text-white transition cursor-pointer"
                        >
                          Create
                        </button>
                      </div>
                    </form>
                  )}

                  {rightPanelType === "import" && (
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/50 flex flex-col gap-1 text-xs">
                        <div className="font-bold text-slate-800">
                          Before uploading the sheet, please ensure that the following fields are included:
                        </div>
                        <div className="text-gray-500 font-medium mt-1">
                          Make sure headers match exactly. Missing fields may cause import errors.
                          <span className="text-blue-500 hover:underline font-bold ml-1.5 cursor-pointer">Show fields</span>
                        </div>
                      </div>

                      <div className="p-4 rounded-xl border border-gray-150 bg-[#F7F6F2] flex items-center justify-between gap-4">
                        <div className="text-xs">
                          <div className="font-bold text-slate-800">Sample Template File</div>
                          <div className="text-gray-400 font-medium">Download the Excel structure.</div>
                        </div>
                        <button
                          onClick={() => toast.success("Sample template downloaded")}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-gray-55 text-xs font-bold text-slate-700 transition cursor-pointer shrink-0"
                        >
                          <IconDownload size={14} className="text-gray-400" />
                          Download
                        </button>
                      </div>

                      {/* Drag drop target */}
                      <div className="border-2 border-dashed border-gray-200 hover:border-[#FBD037] transition rounded-2xl p-8 flex flex-col items-center justify-center gap-2 cursor-pointer bg-slate-50/20">
                        <IconUpload size={28} className="text-gray-400" />
                        <div className="text-xs font-bold text-slate-800 text-center">
                          Click or drag file here
                        </div>
                        <button className="mt-1 px-3.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200/80 text-[10px] font-black uppercase text-slate-600 transition tracking-wide">
                          Choose file
                        </button>
                      </div>

                      {/* Action buttons */}
                      <div className="pt-2 flex justify-end gap-2.5">
                        <button
                          onClick={() => setIsRightPanelOpen(false)}
                          className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer bg-white"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            setIsRightPanelOpen(false);
                            toast.success("Excel editor opened");
                          }}
                          className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm bg-slate-600 hover:bg-slate-700 text-white transition cursor-pointer"
                        >
                          Open Editor
                        </button>
                      </div>
                    </div>
                  )}

                  {rightPanelType === "filter" && (
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold block mb-1">Loan Type</label>
                        <select
                          value={filterLoanType}
                          onChange={(e) => setFilterLoanType(e.target.value)}
                          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                        >
                          <option value="">All Types</option>
                          <option value="Loan">Loan</option>
                          <option value="Advance">Advance</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold block mb-1">Status</label>
                        <select
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                        >
                          <option value="">All Statuses</option>
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Completed">Completed</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Closed">Closed</option>
                          <option value="Disbursed">Disbursed</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold block mb-1">Approved By</label>
                        <input
                          type="text"
                          placeholder="Enter name"
                          value={filterApprovedBy}
                          onChange={(e) => setFilterApprovedBy(e.target.value)}
                          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold block mb-1">Start Date</label>
                        <input
                          type="date"
                          value={filterStartDate}
                          onChange={(e) => setFilterStartDate(e.target.value)}
                          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[#1E293B] text-xs font-semibold block mb-1">End Date</label>
                        <input
                          type="date"
                          value={filterEndDate}
                          onChange={(e) => setFilterEndDate(e.target.value)}
                          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                        />
                      </div>

                      <div className="pt-2 flex justify-between gap-2.5">
                        <button
                          onClick={() => {
                            setFilterLoanType("");
                            setFilterStatus("");
                            setFilterApprovedBy("");
                            setFilterStartDate("");
                            setFilterEndDate("");
                            toast.success("Filters cleared");
                          }}
                          className="px-4 py-2 border border-gray-200 hover:bg-gray-55 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer bg-white"
                        >
                          Clear All
                        </button>
                        <button
                          onClick={() => setIsRightPanelOpen(false)}
                          className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm bg-black hover:bg-slate-800 text-white transition cursor-pointer"
                        >
                          Apply Filters
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      ) : (
        /* Detailed Tabbed View */
        selectedLoan && (
          <div className="bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl rounded-b-3xl rounded-tr-3xl border border-white/20 shadow-sm overflow-hidden mb-6 text-slate-700">
            {/* Breadcrumbs header */}
            <div className="px-6 py-3 bg-[#F7F6F2] border-b border-slate-200/80 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-bold uppercase tracking-wider">
                <button onClick={() => setActiveView("list")} className="hover:text-slate-800 transition cursor-pointer">
                  Dashboard
                </button>
                <span>&gt;</span>
                <button onClick={() => setActiveView("list")} className="hover:text-slate-800 transition cursor-pointer">
                  Loan Advances
                </button>
                <span>&gt;</span>
                <span className="text-[#C9A100]">{selectedLoan.loanId}</span>
              </div>
              <button
                onClick={() => setActiveView("list")}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-200/50 rounded-lg border border-gray-200/60 bg-white transition cursor-pointer"
              >
                <IconArrowLeft size={14} />
                Back
              </button>
            </div>

            {/* Details header block */}
            <div className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 gap-4">
              <div>
                <h1 className="text-xl font-extrabold text-slate-800 tracking-tight leading-snug">
                  Loan Advances Details
                </h1>
                <p className="text-xs text-gray-400 font-medium">Manage and review advances information</p>
              </div>
              <span
                className={`text-xs font-bold px-3 py-1 rounded-full border shadow-sm w-fit ${
                  selectedLoan.status === "Disbursed"
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : selectedLoan.status === "Rejected"
                    ? "text-rose-700 bg-rose-50 border-rose-200"
                    : selectedLoan.status === "Pending"
                    ? "text-amber-700 bg-amber-50 border-amber-200"
                    : "text-blue-700 bg-blue-50 border-blue-200"
                }`}
              >
                ● {selectedLoan.status}
              </span>
            </div>

            {/* Tab selection switches */}
            <div className="flex border-b border-slate-100 bg-slate-50/50 px-6">
              <button
                onClick={() => setActiveDetailTab("overview")}
                className={`py-3 px-4 text-xs font-bold transition-all relative border-b-2 cursor-pointer ${
                  activeDetailTab === "overview"
                    ? "border-[#FBD037] text-slate-800 font-black"
                    : "border-transparent text-gray-400 hover:text-slate-600"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveDetailTab("history")}
                className={`py-3 px-4 text-xs font-bold transition-all relative border-b-2 cursor-pointer ${
                  activeDetailTab === "history"
                    ? "border-[#FBD037] text-slate-800 font-black"
                    : "border-transparent text-gray-400 hover:text-slate-600"
                }`}
              >
                History
              </button>
            </div>

            {/* Tab content bodies */}
            <div className="p-6">
              {activeDetailTab === "overview" ? (
                <div className="space-y-6">
                  {/* Grid layout for cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* Employee profile details */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                      <div className="px-5 py-3.5 bg-slate-50 border-b border-gray-100">
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider text-left">Employee Details</h3>
                      </div>
                      <div className="p-5 space-y-3.5 text-xs font-medium text-slate-700">
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Name:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.name}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Work Email:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.email}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Personal Email:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.personalEmail || "N/A"}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Phone:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.phone}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Gender:</span>
                          <span className="font-bold text-slate-800 capitalize">{selectedLoan.gender || "male"}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Birth Date:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.birthDate || "N/A"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Role:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.role || "N/A"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Approved / Rejected details */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                      <div className="px-5 py-3.5 bg-slate-50 border-b border-gray-100">
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider text-left">Approved / Rejected By Details</h3>
                      </div>
                      <div className="p-5 space-y-3.5 text-xs font-medium text-slate-700">
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Name:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.approvedBy === "-" ? "N/A" : selectedLoan.approvedBy}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Work Email:</span>
                          <span className="font-bold text-slate-800">
                            {selectedLoan.approvedBy === "-" ? "N/A" : `${selectedLoan.approvedBy.toLowerCase().replace(/ /g, "")}@gmail.com`}
                          </span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Personal Email:</span>
                          <span className="font-bold text-slate-800">N/A</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Phone:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.approvedBy === "-" ? "N/A" : "+91 9876543210"}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Gender:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.approvedBy === "-" ? "N/A" : "male"}</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-100 pb-2">
                          <span className="text-gray-400">Birth Date:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.approvedBy === "-" ? "N/A" : "01-10-2000"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Role:</span>
                          <span className="font-bold text-slate-800">{selectedLoan.approvedBy === "-" ? "N/A" : "HR"}</span>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Loan specification details */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-5 py-3.5 bg-slate-50 border-b border-gray-100">
                      <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider text-left">Loan Details</h3>
                    </div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6 text-xs font-medium">
                      <div className="space-y-4">
                        <div>
                          <div className="text-gray-400 font-bold mb-1 text-left">Type:</div>
                          <div className="font-black text-slate-800 text-sm text-left">{selectedLoan.type}</div>
                        </div>
                        <div>
                          <div className="text-gray-400 font-bold mb-1 text-left">Duration (Months):</div>
                          <div className="font-black text-slate-800 text-sm text-left">{selectedLoan.duration}</div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <div className="text-gray-400 font-bold mb-1 text-left">Amount:</div>
                          <div className="font-black text-slate-800 text-sm text-left">{selectedLoan.amount}</div>
                        </div>
                        <div>
                          <div className="text-gray-400 font-bold mb-1 text-left">EMI Start Date:</div>
                          <div className="font-black text-slate-800 text-sm text-left">{selectedLoan.emiDate}</div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <div className="text-gray-400 font-bold mb-1 text-left">Repayment Method:</div>
                          <div className="font-black text-slate-800 text-sm text-left">{selectedLoan.repayment}</div>
                        </div>
                        <div>
                          <div className="text-gray-400 font-bold mb-1 text-left">Reason:</div>
                          <div className="font-black text-slate-800 text-sm text-left">{selectedLoan.reason}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* History timeline logs list */
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-2xl">
                  <div className="relative border-l border-gray-100 pl-6 ml-3 space-y-8 text-left">
                    
                    {/* Log item 1: Created log */}
                    <div className="relative">
                      <span className="absolute -left-9.5 top-0.5 bg-emerald-50 text-emerald-500 rounded-full p-1 border border-emerald-200">
                        <IconPlus size={12} strokeWidth={3} />
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-800">Created by {selectedLoan.name}</span>
                          <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-slate-50 border border-slate-200 text-gray-400 uppercase tracking-wide">
                            creation
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 font-semibold mt-0.5">
                          Loan request created by {selectedLoan.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5 text-[9px] font-bold text-blue-500 bg-blue-50 w-fit px-2 py-0.5 rounded border border-blue-100">
                          <IconClock size={10} />
                          <span>{selectedLoan.createdDate} - {selectedLoan.createdTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* Log item 2: Status updated logs */}
                    {selectedLoan.status !== "Pending" && (
                      <div className="relative">
                        <span className="absolute -left-9.5 top-0.5 bg-blue-50 text-blue-500 rounded-full p-1 border border-blue-200">
                          <IconCheck size={12} strokeWidth={3} />
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-800">Updated by {selectedLoan.approvedBy}</span>
                            <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-slate-50 border border-slate-200 text-gray-400 uppercase tracking-wide">
                              update
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-400 font-semibold mt-0.5">
                            Status changed from 'Pending' to '{selectedLoan.status}'
                          </p>
                          <div className="flex items-center gap-1.5 mt-1.5 text-[9px] font-bold text-blue-500 bg-blue-50 w-fit px-2 py-0.5 rounded border border-blue-100">
                            <IconClock size={10} />
                            <span>{selectedLoan.approvedDate} - {selectedLoan.updatedTime}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Log item 3: Archived log mock */}
                    {selectedLoan.status === "Rejected" && (
                      <div className="relative">
                        <span className="absolute -left-9.5 top-0.5 bg-slate-100 text-gray-400 rounded-full p-1 border border-slate-200">
                          <IconFileText size={12} strokeWidth={3} />
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-800">isArchived by {selectedLoan.name}</span>
                            <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-slate-50 border border-slate-200 text-gray-400 uppercase tracking-wide">
                              update
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-400 font-semibold mt-0.5">
                            Loan request archived
                          </p>
                          <div className="flex items-center gap-1.5 mt-1.5 text-[9px] font-bold text-blue-500 bg-blue-50 w-fit px-2 py-0.5 rounded border border-blue-100">
                            <IconClock size={10} />
                            <span>{selectedLoan.updatedDate} - {selectedLoan.updatedTime}</span>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* Comments Dialog popup modal */}
      {commentModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2.5px] z-50 flex items-center justify-center animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden border border-slate-100 flex flex-col relative animate-in zoom-in-95 duration-200 text-slate-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0 bg-[#F7F6F2]">
              <h3 className="text-sm font-bold text-[#212121]">Comments</h3>
              <button
                onClick={() => setCommentModalOpen(false)}
                className="p-1 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer bg-white"
              >
                <IconX size={16} />
              </button>
            </div>
            
            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!managerCommentText.trim()) {
                  setCommentError(true);
                  return;
                }
                if (targetLoanId && targetStatusOption) {
                  handleStatusUpdate(targetLoanId, targetStatusOption, managerCommentText);
                  setCommentModalOpen(false);
                }
              }}
              className="p-6 space-y-4"
            >
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide text-left block">
                  Manager Comments <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="Add a brief Comments..."
                  value={managerCommentText}
                  onChange={(e) => {
                    setManagerCommentText(e.target.value);
                    if (e.target.value.trim()) setCommentError(false);
                  }}
                  className={`w-full p-2.5 border rounded-xl text-sm bg-white outline-none text-left transition ${
                    commentError ? "border-red-500 focus:border-red-500" : "border-gray-200 focus:border-[#FBD037]"
                  }`}
                />
                {commentError && (
                  <p className="text-[10px] text-red-500 font-bold text-left mt-0.5">Comments is required</p>
                )}
              </div>

              {/* Action buttons */}
              <div className="pt-2 flex justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setCommentModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer bg-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm bg-black hover:bg-slate-800 text-white transition cursor-pointer"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
